class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.onPageHide = this.onPageHide.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
  }

  onPageHide() {
    if (!this.hasAttribute('open') && !this.hasAttribute('results')) return;
    this.pendingHistoryReset = this.getQuery();
    this.resetSearch();
  }

  onPageShow(event) {
    if (!event.persisted || !this.pendingHistoryReset) return;

    const staleQuery = this.pendingHistoryReset;
    const clearIfStale = () => {
      const query = this.getQuery();
      if (!query.length || query === staleQuery) this.resetSearch();
    };

    clearIfStale();
    window.setTimeout(clearIfStale, 0);
    window.setTimeout(clearIfStale, 50);
  }

  resetSearch() {
    this.currentSearchTerm = '';
    this.historyCleared = true;
    this.close(true);
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
    this.querySelectorAll('[data-cc-nav-search-clear]').forEach((button) => {
      button.hidden = true;
      button.classList.remove('is-visible');
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (this.historyCleared) {
      if (!searchTerm.length) {
        this.close(true);
        return;
      }
      this.historyCleared = false;
    }

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    if (this.historyCleared) {
      this.close(true);
      return;
    }

    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getTitleRelevanceScore(title, query) {
    const normalizedTitle = title.trim().toLowerCase();
    if (normalizedTitle.startsWith(query)) return 0;
    if (normalizedTitle.includes(query)) return 1;
    if (normalizedTitle.split(/\s+/).some((word) => word.startsWith(query))) return 1;
    return 2;
  }

  sortProductResultsByTitleRelevance(resultsMarkup, searchTerm) {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return resultsMarkup;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = resultsMarkup;

    const list = wrapper.querySelector('#predictive-search-results-list');
    if (!list) return resultsMarkup;

    const items = Array.from(list.children);
    const productItems = items.filter((item) => item.querySelector('.predictive-search__item--link'));
    const nonProductItems = items.filter((item) => !item.querySelector('.predictive-search__item--link'));

    productItems.sort((a, b) => {
      const titleA = a.querySelector('.predictive-search__item-heading')?.textContent || '';
      const titleB = b.querySelector('.predictive-search__item-heading')?.textContent || '';
      const scoreDiff = this.getTitleRelevanceScore(titleA, query) - this.getTitleRelevanceScore(titleB, query);
      if (scoreDiff !== 0) return scoreDiff;
      return items.indexOf(a) - items.indexOf(b);
    });

    const maxResults = Number(this.getAttribute('data-max-results')) || 4;
    productItems.slice(maxResults).forEach((item) => item.remove());
    productItems.slice(0, maxResults).forEach((item) => list.appendChild(item));
    nonProductItems.forEach((item) => list.appendChild(item));

    return wrapper.innerHTML;
  }

  getSearchResults(searchTerm) {
    this.currentSearchTerm = searchTerm;
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const fetchLimit = Number(this.getAttribute('data-fetch-limit')) || 10;
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${fetchLimit}&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        const gatedMarkup = this.omitGuestPrices(resultsMarkup);
        this.cachedResults[queryKey] = gatedMarkup;
        this.renderSearchResults(gatedMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  omitGuestPrices(resultsMarkup) {
    if (this.getAttribute('data-show-logged-out-pricing') === 'true') return resultsMarkup;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = resultsMarkup;
    wrapper.querySelectorAll('.predictive-search__item-price--guest').forEach((node) => node.remove());
    return wrapper.innerHTML;
  }

  renderSearchResults(resultsMarkup) {
    if (this.historyCleared || (this.currentSearchTerm || '') !== this.getQuery()) return;

    const sortedMarkup = this.sortProductResultsByTitleRelevance(this.omitGuestPrices(resultsMarkup), this.currentSearchTerm || '');
    this.predictiveSearchResults.innerHTML = sortedMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  } 

  getResultsMaxHeight() {
    const navSection = document.getElementById('shopify-section-nav');
    const headerSection = document.getElementById('shopify-section-header');
    const anchor = navSection || headerSection;

    if (anchor) {
      this.resultsMaxHeight = window.innerHeight - anchor.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = Math.round(window.innerHeight * 0.7);
    }

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) { 
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
